<template>
  <div ref="containerRef" class="file-thumbnail">
    <PreviewSkeleton v-if="isLoading" :label="loadingLabel" />
    <img
      v-if="isImage && shouldLoad && !hasError"
      class="file-thumbnail__image"
      :class="{ 'file-thumbnail__image--loaded': isLoaded }"
      :src="directFileUrl"
      :alt="altText"
      @load="handleLoad"
      @error="handleError"
    />
    <iframe
      v-if="canEmbedPdf && shouldLoad && !hasError"
      class="file-thumbnail__pdf-frame"
      :class="{ 'file-thumbnail__pdf-frame--loaded': isLoaded }"
      :src="pdfEmbedUrl"
      :title="altText"
      tabindex="-1"
      @load="handleLoad"
      @error="handleError"
    ></iframe>
    <div v-if="isEmbedded && shouldLoad" class="file-thumbnail__embed" @click.stop>
      <iframe
        class="file-thumbnail__embed-frame"
        :src="embeddedUrl"
        :title="altText"
        @load="handleLoad"
      ></iframe>
    </div>
    <video
      v-if="isVideo && shouldLoad && !hasError"
      class="file-thumbnail__video"
      :class="{ 'file-thumbnail__video--loaded': isLoaded }"
      controls
      preload="metadata"
      @click.stop
      @loadedmetadata="handleLoad"
      @error="handleError"
    >
      <source :src="directFileUrl" :type="videoMimeType" />
    </video>
    <PreviewFallback
      v-if="shouldShowFallback"
      :extension="extension"
      :file-name="fileName"
    />
  </div>
</template>

<script setup>
import { computed, defineProps, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { getFileExtension } from "@/utils/getFileExtension";
import { getPdfEmbedUrl, hasCleanPdfPath } from "@/utils/getPdfPreviewUrl";
import PreviewFallback from "./PreviewFallback.vue";
import PreviewSkeleton from "./PreviewSkeleton.vue";

const props = defineProps({
  file: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const containerRef = ref(null);
const shouldLoad = ref(false);
const isLoaded = ref(false);
const hasError = ref(false);
let observer = null;
let loadTimeoutId = null;

const fileName = computed(() => props.file?.name || props.file?.title || "File preview");
const loadingLabel = computed(() => t("global.buttons.loading"));

const extension = computed(() => {
  const rawMeta = String(props.file?.extension || "")
    .replace(".", "")
    .toLowerCase();
  const fromMeta = /^[a-z0-9]{1,5}$/.test(rawMeta) ? rawMeta : "";
  const fromPath = getFileExtension(props.file?.file, "");
  const fromName = getFileExtension(props.file?.name || props.file?.title, "");
  const value = fromMeta || fromPath || fromName || props.file?.type || "file";

  return String(value).replace(".", "").toLowerCase();
});

const altText = computed(() => `${fileName.value} preview`);
const directFileUrl = computed(() => props.file?.file || "");
const isImage = computed(() =>
  ["png", "jpg", "jpeg", "webp", "gif"].includes(extension.value),
);
const isPdf = computed(() => extension.value === "pdf");
// Dirty paths like "file.pdf (2)" trigger automatic browser downloads in iframes.
const canEmbedPdf = computed(
  () => isPdf.value && Boolean(directFileUrl.value) && hasCleanPdfPath(directFileUrl.value),
);
const isEmbedded = computed(() => Boolean(props.file?.embeded));
const embeddedUrl = computed(() => props.file?.embeded || "");
const isVideo = computed(() => {
  if (isEmbedded.value) {
    return false;
  }

  if (props.file?.type === "video") {
    return true;
  }

  return ["mp4", "webm", "mov", "avi", "mkv", "m4v"].includes(extension.value);
});
const videoMimeType = computed(() => {
  const mimeTypes = {
    mp4: "video/mp4",
    webm: "video/webm",
    mov: "video/quicktime",
    avi: "video/x-msvideo",
    mkv: "video/x-matroska",
    m4v: "video/x-m4v",
  };

  return mimeTypes[extension.value] || "video/mp4";
});
const isLoading = computed(() => {
  if (hasError.value || isLoaded.value) {
    return false;
  }

  if (!shouldLoad.value) {
    return true;
  }

  return isImage.value || canEmbedPdf.value || isVideo.value || isEmbedded.value;
});
const shouldShowFallback = computed(
  () =>
    hasError.value ||
    (shouldLoad.value &&
      !isImage.value &&
      !canEmbedPdf.value &&
      !isVideo.value &&
      !isEmbedded.value),
);
const pdfEmbedUrl = computed(() => getPdfEmbedUrl(directFileUrl.value));

const clearLoadTimeout = () => {
  if (loadTimeoutId) {
    window.clearTimeout(loadTimeoutId);
    loadTimeoutId = null;
  }
};

const handleLoad = () => {
  clearLoadTimeout();
  isLoaded.value = true;
};

const handleError = () => {
  clearLoadTimeout();
  hasError.value = true;
};

const startLoadTimeout = () => {
  clearLoadTimeout();
  loadTimeoutId = window.setTimeout(() => {
    if (!isLoaded.value && !hasError.value) {
      isLoaded.value = true;
    }
  }, 20000);
};

onMounted(() => {
  const enableLoad = () => {
    if (shouldLoad.value) {
      return;
    }

    shouldLoad.value = true;
    startLoadTimeout();
  };

  if (!("IntersectionObserver" in window)) {
    enableLoad();
    return;
  }

  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        enableLoad();
        observer?.disconnect();
      }
    },
    { rootMargin: "160px 0px" },
  );

  if (containerRef.value) {
    observer.observe(containerRef.value);
  }

  window.setTimeout(() => {
    if (!shouldLoad.value) {
      enableLoad();
      observer?.disconnect();
    }
  }, 250);
});

onBeforeUnmount(() => {
  clearLoadTimeout();
  observer?.disconnect();
});
</script>

<style scoped lang="scss">
@import "@/styles/document-preview/file-thumbnail";
</style>
